import { useFilePicker } from "@/shared/hooks/useFilePicker";
import { useObjectURL } from "@/shared/hooks/useObjectURL";
import { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import ImageCropper from "../base/ImageCropper";

type Props = {
  onAvatarChange: (file: File | null) => void;
  avatarUrl?: string | null;
  deleteAvatar?: () => void;
};

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export default function AvatarUploader(props: Props) {
  const { onAvatarChange, avatarUrl: initialAvatarUrl, deleteAvatar } = props;
  const { t } = useTranslation();
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [fileToProcess, setFileToProcess] = useState<Blob | null>(null);
  const generatedUrl = useObjectURL(avatarFile ?? undefined);

  // Sync with initial avatar URL
  useEffect(() => {
    setAvatarUrl(initialAvatarUrl ?? null);
  }, [initialAvatarUrl]);

  // Update the displayed URL when file changes
  useEffect(() => {
    if (generatedUrl) {
      setAvatarUrl(generatedUrl);
    }
  }, [generatedUrl]);

  const handleRawFile = useCallback(
    (file: File) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        showCustomToast(
          t("avatar_file_too_large", { size: MAX_FILE_SIZE_MB }),
          "error"
        );
        return;
      }

      // Set the file to be processed by the cropper
      setFileToProcess(file);
    },
    [t]
  );

  const handleCroppedFile = useCallback(
    (croppedFile: File) => {
      setAvatarFile(croppedFile);
      onAvatarChange(croppedFile);
      setAvatarUrl(URL.createObjectURL(croppedFile));
      setFileToProcess(null); // Close the cropper
    },
    [onAvatarChange]
  );

  const handleCropperClose = useCallback(() => {
    setFileToProcess(null);
  }, []);

  const handleRemove = useCallback(() => {
    setAvatarFile(null);
    setAvatarUrl(null);
    deleteAvatar?.();
    onAvatarChange(null);
  }, [onAvatarChange, deleteAvatar]);

  const pickFile = useFilePicker(handleRawFile);

  const handleImagePicker = () =>
    pickFile("image/jpg,image/jpeg,image/png,image/webp,image/heic");

  const displayUrl = avatarUrl || "images/default/avatar.svg";
  return (
    <div className="flex-center">
      <div className="flex flex-col gap-2 items-center">
        <ImageCropper
          file={fileToProcess as Blob}
          onClose={handleCropperClose}
          onChange={handleCroppedFile}
        />

        <VibratingButton
          type="button"
          className="size-[60px] relative group"
          onClick={handleImagePicker}
        >
          <img
            src={displayUrl}
            alt="Avatar"
            className="size-full object-cover rounded-2xl border-b bg-white/08 border-white/08"
          />
        </VibratingButton>

        {avatarUrl && avatarUrl !== "images/default/avatar.svg" && (
          <div className="flex gap-2">
            <VibratingButton
              type="button"
              className="text-sm text-blue px-8 py-2 rounded-10"
              style={{ background: "rgba(37, 133, 255, 0.12)" }}
              onClick={handleImagePicker}
            >
              {t("avatar_action.change")}
            </VibratingButton>
            <VibratingButton
              type="button"
              className="text-sm text-red px-8 py-2 rounded-10"
              onClick={handleRemove}
              style={{ background: "rgba(255, 53, 53, 0.12)" }}
            >
              {t("avatar_action.remove")}
            </VibratingButton>
          </div>
        )}
      </div>
    </div>
  );
}
